import {
  AllowedIpAddress,
  Department,
  IntakeChannel,
  ReportType,
  RoleSettings,
  TenantFlags,
  TenantNoteCategory,
  TenantProgram,
  TenantUser,
  UpdateReportForm,
  UserTenant,
  allowedIpAddressSchema,
  tenantFlagsSchema,
  SubmitCustomerTicket,
  CustomErrorTypes,
  ChannelCountries,
  ReportFormData,
  UpdateReportType,
  IntakeChannelProgramLogo,
  IntakeChannelProgramDetails,
  CommentResponse,
  UpdateTenantFlags,
  RoleSettingsUpdate,
  TabPayload,
  NewTabPayload,
  IntakeChannelDetails,
  InviteUserPayload,
} from './types';

import { LocalizedChannelDetails } from '../localization/type';
import {
  PaginatedResponse,
  parsePaginatedResponse,
  restAPI,
} from '../rest-api';
import { ScheduledMeeting } from '../schedules/types';

import {
  FormFieldResponseArray,
  FormFieldUpdateRequest,
} from '~/pages/customer/settings/report-form/schema';
import { FormFieldResponseArray as NewFormFieldResponseArray, FormFieldUpdateRequest as NewFormFieldUpdateRequest } from '~/pages/customer/settings/report-form-v2/schema';
import { stripUndefined } from '~/utils/helpers';
import { RootState } from '~/utils/store';
import { AtLeastOne } from '~/utils/type-helpers';

const endpoints = restAPI.injectEndpoints({
  endpoints: (builder) => ({
    userTenant: builder.query<UserTenant, void>({
      query: () => ({ url: '/tenant' }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'user-tenant' }] : [],
    }),
    updateTenant: builder.mutation<
      UserTenant,
      AtLeastOne<Omit<UserTenant, 'slug'>> & Pick<UserTenant, 'slug'>
    >({
      query: (payload) => ({
        url: `/tenant/${payload.slug}`,
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'user-tenant' }] : [],
    }),
    updateUserTenant: builder.mutation<void, { tenant_id: number }>({
      query: (args) => ({
        url: '/user/profile/update',
        method: 'PATCH',
        data: args,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'intake-channels' }] : [],
    }),
    tenantUsers: builder.query<
      { account_settings_user_list: Array<TenantUser> },
      { tenantSlug: string; params?: { all: string } }
    >({
      query: (arg) => {
        return {
          url: `/tenant/${arg.tenantSlug}/users`,
          params: stripUndefined(arg.params ?? {}),
        };
      },
      providesTags: (res) => (res ? [{ type: 'Tenant', id: 'user-list' }] : []),
    }),
    updateTenantUser: builder.mutation<
      Array<TenantUser>,
      {
        users: Array<
          TenantUser & {
            department_id?: number;
          }
        >;
        tenantSlug: string;
      }
    >({
      query: ({ tenantSlug, users }) => ({
        url: `/tenant/${tenantSlug}/update-user`,
        method: 'PATCH',
        data: {
          users: users.map((user) => {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const { department_id, ...rest } = user;
            return {
              ...rest,
              department: department_id,
            };
          }),
        },
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'user-list' }] : [],
    }),
    tenantFlags: builder.query<TenantFlags, void>({
      async queryFn(_arg, api, _extraOptions, baseQuery) {
        const slug = (api.getState() as RootState).auth.user.slug;
        const result = await baseQuery({ url: `/tenant/${slug}/flags` });
        const validated = tenantFlagsSchema.safeParse(result.data);

        if (result.error || !validated.success) {
          const error = result.error ?? {
            status: 'CUSTOM_ERROR',
            error: 'Invalid data received from server',
          };

          return { error };
        }

        return { data: validated.data };
      },
    }),
    tenantMeetings: builder.query<PaginatedResponse<ScheduledMeeting>, void>({
      query() {
        return { url: '/schedule/meetings' };
      },
    }),
    // Returns all `categories` created under the tenant
    tenantNoteCategories: builder.query<Array<TenantNoteCategory>, void>({
      query: () => ({ url: '/tenant-note-categories' }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'note-categories' }] : [],
    }),
    roleSettings: builder.query<
      RoleSettings,
      { tenantSlug: string; userId: string; channelId: string }
    >({
      query: ({ tenantSlug, userId, channelId }) => ({
        url: `/tenant/${tenantSlug}/users/${userId}/role-settings?channel_id=${channelId}`,
      }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'role-settings' }] : [],
    }),
    updateRoleSettings: builder.mutation<
      void,
      {
        slug: string;
        userId: number;
        payload: AtLeastOne<
          RoleSettingsUpdate &
            Omit<RoleSettings, 'allowed_form_type_ids'> & {
              allowed_report_type_ids?: Array<number>;
            }
        >;
      }
    >({
      query: ({ slug, userId, payload }) => ({
        url: `/tenant/${slug}/users/${userId}/role-settings`,
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'role-settings' }] : [],
    }),
    departmentList: builder.query<
      Department[],
      {
        tenantSlug: string;
        params?: {
          language_id?: number;
        };
      }
    >({
      query: (args) => {
        return {
          url: `/tenant/${args.tenantSlug}/department`,
          params: stripUndefined(args.params ?? {}),
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'department-list' }] : [],
    }),
    inviteUser: builder.mutation<void, InviteUserPayload>({
      query(arg) {
        const { slug, ...rest } = arg;
        return {
          url: `/tenant/${slug}/invite-user`,
          method: 'POST',
          data: rest,
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'user-list' }] : [],
    }),
    tenantCountries: builder.query<
      Array<{
        code: string;
        name: string;
      }>,
      void
    >({
      query: () => ({ url: '/countries' }),
      providesTags: (res) => (res ? [{ type: 'Tenant', id: 'countries' }] : []),
    }),
    tenantAllowedCountries: builder.query<
      Array<{
        code: string;
        name: string;
      }>,
      { tenantSlug: string }
    >({
      query: ({ tenantSlug }) => ({ url: `/${tenantSlug}/countries` }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'allowed-countries' }] : [],
    }),
    reportTypes: builder.query<Array<ReportType>, { tenantSlug?: string }>({
      query: ({ tenantSlug }) => ({ url: `/${tenantSlug}/report-types` }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'report-types' }] : [],
    }),
    allowedIPAddresses: builder.query<
      PaginatedResponse<AllowedIpAddress>,
      void
    >({
      query: () => ({ url: '/allowed-ip-address' }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'allowed-ip-address' }] : [],
      transformResponse: (response) => {
        return parsePaginatedResponse(allowedIpAddressSchema, response);
      },
    }),
    updateReportFormConfig: builder.mutation<
      void,
      {
        slug: string;
        payload: {
          is_show_non_whistleblowing_message: boolean;
          non_whistleblowing_message: string;
        };
      }
    >({
      query: ({ slug, payload }) => ({
        url: `/tenant/${slug}/settings/report-form`,
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Reports', id: 'report-form-config' }] : [],
    }),
    intakeChannels: builder.query<Array<IntakeChannel>, { slug: string }>({
      query: ({ slug }) => ({ url: `/intake-channel/tenant/${slug}` }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'intake-channels' }] : [],
    }),
    // INTAKE CHANNEL LOGO AND BANNER
    intakeChannelProgramLogo: builder.query<
      {
        // logo_url: string;
        banner_url: string;
      },
      {
        slug: string;
        intakeChannelId: number;
      }
    >({
      query: (arg) => ({
        url: `/intake-channel/tenant/${arg.slug}/${arg.intakeChannelId}/logo`,
      }),
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'intake-channels-logo' }] : [],
    }),
    // PROGRAM TABS AND REPORT TYPES FOR INTAKE CHANNEL
    intakeChannelProgramDetails: builder.query<
      IntakeChannelDetails,
      {
        slug: string;
        intakeChannelId: number;
        params?: {
          language_id?: number;
        };
      }
    >({
      query: (args) => ({
        url: `/intake-channel/tenant/${args.slug}/${args.intakeChannelId}`,
        params: stripUndefined(args.params ?? {}),
      }),
      providesTags: (res, _err, arg) =>
        res
          ? [
              { type: 'Tenant', id: 'channel-program-details' },
              {
                type: 'Tenant',
                id: `${arg.slug}-${arg.intakeChannelId}-program-details`,
              },
            ]
          : [],
    }),
    // UPDATE INTAKE CHANNEL REPORT SETTINGS
    updateIntakeChannelFlags: builder.mutation<
      void,
      {
        slug: string;
        intakeChannelId: number;
        payload: Partial<UpdateTenantFlags>;
      }
    >({
      query({ slug, intakeChannelId, payload }) {
        return {
          url: `/intake-channel/tenant/${slug}/${intakeChannelId}`,
          method: 'PATCH',
          data: payload,
        };
      },
      invalidatesTags: (res, _err, arg) =>
        res
          ? [
              {
                type: 'Tenant',
                id: `${arg.slug}-${arg.intakeChannelId}-program-details`,
              },
              { type: 'Localization', id: 'localized-channel-details' },
              {
                type: 'Localization',
                id: `localized-channel-${arg.intakeChannelId}-details`,
              },
              { type: 'Tenant', id: `channel-details-${arg.intakeChannelId}` },
            ]
          : [],
    }),
    // UPDATE CHANNEL TABS
    updateChannelTabs: builder.mutation<
      void,
      {
        data: {
          created_data?: Array<NewTabPayload>;
          tabs?: Array<TabPayload>;
          delete_tab_ids?: Array<number>;
        };
        intakeChannelId: number;
      }
    >({
      query(arg) {
        return {
          url: `/intake-channel/${arg.intakeChannelId}/program-settings-tab`,
          method: 'PATCH',
          data: arg.data,
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'channel-program-details' }] : [],
    }),
    // ADD CHANNEL CUSTOM FIELD
    addChannelCustomField: builder.mutation<
      void,
      {
        data: {
          title: string;
          type: number;
          dropdown_fields?: Array<string>;
        };
        slug: string;
        intakeChannelId: number;
      }
    >({
      query(arg) {
        return {
          url: `/intake-channel/tenant/${arg.slug}/${arg.intakeChannelId}/report-custom-field`,
          method: 'POST',
          data: arg.data,
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'channel-program-details' }] : [],
    }),
    // UPDATE CHANNEL CUSTOM FIELD
    updateChannelCustomField: builder.mutation<
      void,
      {
        data: {
          id: number;
          title: string;
          type: number;
          dropdown_fields?: Array<string>;
        };
        slug: string;
      }
    >({
      query(arg) {
        return {
          url: `/tenant/${arg.slug}/settings/report-form`,
          method: 'PATCH',
          data: arg.data,
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'channel-program-details' }] : [],
    }),
    // DELETE CHANNEL CUSTOM FIELD
    deleteChannelCustomField: builder.mutation<
      void,
      {
        id: number;
        slug: string;
      }
    >({
      query(arg) {
        return {
          url: `/tenant/${arg.slug}/settings/report-form`,
          method: 'PATCH',
          data: { delete_report_custom_field_ids: [arg.id] },
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'channel-program-details' }] : [],
    }),
    createReportType: builder.mutation<
      void,
      {
        intakeChannelId: number;
        name: string;
      }
    >({
      query(arg) {
        return {
          url: `/intake-channel/${arg.intakeChannelId}/report-types`,
          method: 'POST',
          data: { name: arg.name },
        };
      },
      invalidatesTags: [{ type: 'Tenant', id: 'channel-program-details' }],
    }),
    // Deprecated
    getIntakeChannelReportFormData: builder.query<
      FormFieldResponseArray,
      { id: number }
    >({
      query: (arg) => ({
        url: `/intake-channel/report-form/${arg.id}`,
      }),
      providesTags: (res, _, arg) =>
        res ? [{ type: 'Tenant', id: `channel-report-form-${arg.id}` }] : [],
    }),
    updateIntakeChannelReportForm: builder.mutation<
      void,
      { channelID: number; fields: FormFieldUpdateRequest }
    >({
      query(arg) {
        return {
          url: `/intake-channel/report-form/update/${arg.channelID}`,
          method: 'PATCH',
          data: arg.fields,
        };
      },
      invalidatesTags: (res, _, arg) =>
        res
          ? [{ type: 'Tenant', id: `channel-report-form-${arg.channelID}` }]
          : [],
    }),


    getNewIntakeChannelReportFormData: builder.query<
      { report_form_config_data: NewFormFieldResponseArray },
      { id: number }
    >({
      query: (arg) => ({
        url: `/v2/intake-channel/report-form/${arg.id}`,
      }),
      providesTags: (res, _, arg) =>
        res ? [{ type: 'Tenant', id: `channel-report-form-${arg.id}` }] : [],
    }),

    updateNewIntakeChannelReportForm: builder.mutation<
      void,
      { channelID: number; fields: NewFormFieldUpdateRequest }
    >({
      query(arg) {
        return {
          url: `/v2/intake-channel/report-form/update/${arg.channelID}`,
          method: 'PATCH',
          data: arg.fields,
        };
      },
      invalidatesTags: (res, _, arg) =>
        res
          ? [{ type: 'Tenant', id: `channel-report-form-${arg.channelID}` }]
          : [],
    }),
    getReportFormData: builder.query<ReportFormData, { slug: string }>({
      query: (arg) => {
        return {
          url: `/tenant/${arg.slug}/settings/report-form`,
        };
      },
    }),
    updateReportType: builder.mutation<
      void,
      {
        slug: string;
        report_type_id: number;
        data: UpdateReportType;
      }
    >({
      query(arg) {
        return {
          url: `/tenant/${arg.slug}/report-type/${arg.report_type_id}`,
          method: 'PATCH',
          data: arg.data,
        };
      },
      invalidatesTags: [{ type: 'Tenant', id: 'channel-program-details' }],
    }),
    updateReportForm: builder.mutation<
      void,
      {
        slug: string;
        data: UpdateReportForm;
      }
    >({
      query(arg) {
        return {
          url: `/tenant/${arg.slug}/settings/report-form`,
          method: 'PATCH',
          data: arg.data,
        };
      },
      invalidatesTags: [{ type: 'Tenant', id: 'channel-program-details' }],
    }),
    tenantPublicProgram: builder.query<
      TenantProgram,
      { tenantSlug: string; languageId?: number }
    >({
      query: ({ tenantSlug, languageId }) => ({
        url: `/${tenantSlug}/program-overview`,
        params: stripUndefined({ language_id: languageId }),
      }),
      extraOptions: { requireAuth: false },
    }),
    // GET PROGRAM POLICY
    programTenant: builder.query<
      TenantProgram,
      {
        slug: string;
      }
    >({
      query: (arg) => {
        return {
          url: `/tenant/${arg.slug}`,
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'tenant-program' }] : [],
    }),
    getErrorTypes: builder.query<CustomErrorTypes[], void>({
      query: () => {
        return {
          url: '/error-log-types',
        };
      },
    }),
    // ADD CUSTOMER TICKET
    submitCustomerTicket: builder.mutation<void, SubmitCustomerTicket>({
      query: (arg) => {
        return {
          url: '/tenant/customer-support-ticket',
          method: 'POST',
          data: arg,
        };
      },
    }),
    // GET COUNTRIES FOR INTAKE CHANNEL
    channelCountries: builder.query<
      ChannelCountries[],
      { slug: string; channelId: number }
    >({
      query: (arg) => {
        return {
          url: `/intake-channel/tenant/${arg.slug}/${arg.channelId}/countries`,
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'intake-channels-countries' }] : [],
    }),
    // ADD COUNTRY TO CHANNEL
    addChannelCountry: builder.mutation<
      void,
      {
        slug: string;
        channelId: number;
        payload: {
          code: string;
        };
      }
    >({
      query: (arg) => {
        return {
          url: `/intake-channel/tenant/${arg.slug}/${arg.channelId}/countries`,
          method: 'POST',
          data: arg.payload,
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'intake-channels-countries' }] : [],
    }),
    // UPDATE TOGGLE COUNTRY IN CHANNEL
    updateCountryToggle: builder.mutation<
      void,
      {
        slug: string;
        channelId: number;
        countryId: number;
      }
    >({
      query: (arg) => {
        return {
          url: `/intake-channel/tenant/${arg.slug}/${arg.channelId}/countries/${arg.countryId}/toggle`,
          method: 'PATCH',
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'intake-channels-countries' }] : [],
    }),
    updateCountryScore: builder.mutation<
      void,
      {
        slug: string;
        channelId: number;
        countryId: number;
        score: number;
      }
    >({
      query: (arg) => {
        return {
          url: `/intake-channel/tenant/${arg.slug}/${arg.channelId}/countries/${arg.countryId}`,
          method: 'PATCH',
          data: {
            risk_score: arg.score,
          },
        };
      },
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'intake-channels-countries' }] : [],
    }),
    // DELETE COUNTRY IN CHANNEL
    deleteChannelCountry: builder.mutation<
      void,
      {
        slug: string;
        channelId: number;
        countryId: number;
      }
    >({
      query: (arg) => {
        return {
          url: `/intake-channel/tenant/${arg.slug}/${arg.channelId}/countries/${arg.countryId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [{ type: 'Tenant', id: 'intake-channels-countries' }],
    }),
    // CUSTOM FETCH FOR PROGRAM DETAILS
    getTenantProgramDetails: builder.query<
      {
        programTenant: TenantProgram;
        intakeChannelProgramLogo: IntakeChannelProgramLogo;
        intakeChannelProgramDetails: IntakeChannelProgramDetails;
        localizedChannelDetails: LocalizedChannelDetails[];
      },
      {
        slug: string;
        channelId: number;
        params?: {
          language_id: number;
        };
      }
    >({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const [
          programTenantResult,
          intakeChannelProgramLogoResult,
          intakeChannelProgramDetailsResult,
          intakeChannelLocalizedDetailsResult,
        ] = await Promise.all([
          baseQuery({
            url: `/tenant/${arg.slug}`,
            params: stripUndefined(arg.params ?? {}),
          }),
          baseQuery({
            url: `/intake-channel/tenant/${arg.slug}/${arg.channelId}/logo`,
          }),
          baseQuery({
            url: `/intake-channel/tenant/${arg.slug}/${arg.channelId}`,
            params: stripUndefined(arg.params ?? {}),
          }),
          baseQuery({
            url: `/intake-channel/tenant/${arg.slug}/${arg.channelId}/localization/form-type-tenant-management`,
          }),
        ]);

        if (
          programTenantResult.error ||
          intakeChannelProgramLogoResult.error ||
          intakeChannelProgramDetailsResult.error ||
          intakeChannelLocalizedDetailsResult.error
        ) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Invalid data received from server',
            } as const,
          };
        }

        return {
          data: {
            programTenant: programTenantResult.data as TenantProgram,
            intakeChannelProgramLogo:
              intakeChannelProgramLogoResult.data as IntakeChannelProgramLogo,
            intakeChannelProgramDetails:
              intakeChannelProgramDetailsResult.data as IntakeChannelProgramDetails,
            localizedChannelDetails:
              intakeChannelLocalizedDetailsResult.data as LocalizedChannelDetails[],
          },
        };
      },
      providesTags: [{ type: 'Tenant', id: 'program-details' }],
    }),
    getChannelDetails: builder.query<
      IntakeChannelProgramDetails,
      {
        slug: string;
        channelId: number;
        params?: {
          language_id: number;
        };
      }
    >({
      query: ({ slug, channelId, params }) => ({
        url: `/intake-channel/tenant/${slug}/${channelId}`,
        params: stripUndefined(params ?? {}),
      }),
      providesTags: (res, _err, arg) =>
        res ? [{ type: 'Tenant', id: `channel-details-${arg.channelId}` }] : [],
    }),
    getInfographicPresets: builder.query<{ [key: string]: string }, void>({
      query: () => ({ url: '/tenant/infographics/presets' }),
    }),
    //POLICY COMMENTS ENDPOINTS
    getComments: builder.query<
      CommentResponse[],
      {
        slug: string;
        channelId: number;
        params?: { search: string; mentions_only: string };
      }
    >({
      query: ({ slug, channelId, params }) => ({
        url: `/intake-channel/tenant/${slug}/${channelId}/policy-comments`,
        params: params,
      }),
      providesTags: [{ type: 'Tenant', id: 'program-policy-comments' }],
    }),
    addPolicyComment: builder.mutation<
      CommentResponse,
      {
        slug: string;
        channelId: number;
        payload: {
          content: string;
          highlighted_text: string;
          tagged_users: number[];
        };
      }
    >({
      query: ({ slug, channelId, payload }) => ({
        url: `/intake-channel/tenant/${slug}/${channelId}/policy-comments`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'program-policy-comments' }] : [],
    }),
    removePolicyComment: builder.mutation<
      void,
      {
        slug: string;
        channelId: number;
        commentId: string;
      }
    >({
      query: ({ slug, channelId, commentId }) => ({
        url: `/intake-channel/tenant/${slug}/${channelId}/policy-comments/${commentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Tenant', id: 'program-policy-comments' }],
    }),
    resolvePolicyComment: builder.mutation<
      void,
      {
        slug: string;
        channelId: number;
        commentId: string;
        payload: { is_resolved: boolean };
      }
    >({
      query: ({ slug, channelId, commentId, payload }) => ({
        url: `/intake-channel/tenant/${slug}/${channelId}/policy-comments/${commentId}`,
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: [{ type: 'Tenant', id: 'program-policy-comments' }],
    }),
    createDepartment: builder.mutation<
      Department,
      Pick<Department, 'name'> & { slug: string }
    >({
      query: ({ slug, ...payload }) => ({
        url: `/tenant/${slug}/department`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (_res, err) =>
        !err ? [{ type: 'Tenant', id: 'department-list' }] : [],
    }),
    updateDepartment: builder.mutation<
      void,
      AtLeastOne<Department> & Pick<Department, 'id'> & { slug: string }
    >({
      query: ({ id, slug, ...department }) => ({
        url: `/tenant/${slug}/department/${id}`,
        method: 'PATCH',
        data: department,
      }),
      invalidatesTags: (_res, err) =>
        !err ? [{ type: 'Tenant', id: 'department-list' }] : [],
    }),
    deleteDepartment: builder.mutation<
      void,
      Pick<Department, 'id'> & { slug: string }
    >({
      query: ({ id, slug }) => ({
        url: `/tenant/${slug}/department/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, err) =>
        !err ? [{ type: 'Tenant', id: 'department-list' }] : [],
    }),
  }),
  overrideExisting: false,
});

export const TenantService = endpoints;
